import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './ViewNicknames.css';

interface UserNicknameInfo {
  userId: string;
  email: string;
  displayName?: string;
  nickname?: string;
  role: string;
  createdAt?: any;
}

interface ViewNicknamesProps {
  isOpen: boolean;
  onClose: () => void;
}

const ViewNicknames: React.FC<ViewNicknamesProps> = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState<UserNicknameInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAllUsers();
    }
  }, [isOpen]);

  const loadAllUsers = async () => {
    setLoading(true);
    try {
      const userProfilesRef = collection(db, 'userProfiles');
      const snapshot = await getDocs(userProfilesRef);
      
      const userList: UserNicknameInfo[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        userList.push({
          userId: doc.id,
          email: data.email || 'N/A',
          displayName: data.displayName,
          nickname: data.nickname,
          role: data.role || 'USER',
          createdAt: data.createdAt
        });
      });

      // Sort by nickname
      userList.sort((a, b) => {
        const nickA = a.nickname || 'ZZZ';
        const nickB = b.nickname || 'ZZZ';
        return nickA.localeCompare(nickB);
      });

      setUsers(userList);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (user.nickname?.toLowerCase().includes(searchLower)) ||
      (user.email?.toLowerCase().includes(searchLower)) ||
      (user.displayName?.toLowerCase().includes(searchLower)) ||
      (user.role?.toLowerCase().includes(searchLower))
    );
  });

  const handleMigrateSelectedUsers = async () => {
    if (selectedUserIds.size === 0) {
      window.alert('Please select at least one user without a nickname.');
      return;
    }

    if (!window.confirm(`Generate nicknames for ${selectedUserIds.size} selected user(s)? This cannot be undone.`)) {
      return;
    }

    setMigrating(true);
    try {
      const { updateDoc, doc } = await import('firebase/firestore');
      const { NicknameService } = await import('../services/nicknameService');
      
      let migratedCount = 0;
      
      for (const userId of Array.from(selectedUserIds)) {
        const user = users.find(u => u.userId === userId);
        if (user && !user.nickname) {
          // Generate nickname
          const displayName = user.displayName || user.email.split('@')[0];
          const newNickname = await NicknameService.generateUniqueNickname(displayName, user.email);
          
          // Update Firestore
          await updateDoc(doc(db, 'userProfiles', user.userId), {
            nickname: newNickname
          });
          
          console.log(`✅ Generated nickname for ${user.email}:`, newNickname);
          migratedCount++;
        }
      }
      
      window.alert(`✅ Successfully generated nicknames for ${migratedCount} user(s)!`);
      setSelectedUserIds(new Set()); // Clear selection
      loadAllUsers(); // Reload to show updated data
    } catch (error) {
      console.error('Error migrating users:', error);
      window.alert('❌ Error generating nicknames. Check console for details.');
    } finally {
      setMigrating(false);
    }
  };

  const handleToggleUser = (userId: string) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUserIds(newSelection);
  };

  const handleSelectAllWithoutNickname = () => {
    const usersWithoutNickname = users.filter(u => !u.nickname);
    if (usersWithoutNickname.length === selectedUserIds.size) {
      setSelectedUserIds(new Set()); // Deselect all
    } else {
      setSelectedUserIds(new Set(usersWithoutNickname.map(u => u.userId)));
    }
  };

  const handleSelectAllUsers = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set()); // Deselect all
    } else {
      setSelectedUserIds(new Set(users.map(u => u.userId)));
    }
  };

  const handleDeleteSelectedUsers = async () => {
    if (selectedUserIds.size === 0) {
      alert('Please select users to delete');
      return;
    }

    // Check if trying to delete MASTER user with nickname
    const selectedUsers = users.filter(u => selectedUserIds.has(u.userId));
    const masterWithNickname = selectedUsers.find(u => u.role === 'MASTER' && u.nickname);
    
    if (masterWithNickname) {
      alert(`❌ Cannot delete MASTER user "${masterWithNickname.nickname}" (${masterWithNickname.email})\n\nPlease deselect this user before proceeding.`);
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedUserIds.size} selected user(s)?\n\nThis action cannot be undone!`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    try {
      const { deleteDoc, doc } = await import('firebase/firestore');
      
      const deletePromises = Array.from(selectedUserIds).map(async (userId) => {
        try {
          await deleteDoc(doc(db, 'userProfiles', userId));
          console.log(`✅ Deleted user profile: ${userId}`);
        } catch (error) {
          console.error(`❌ Error deleting user ${userId}:`, error);
          throw error;
        }
      });

      await Promise.all(deletePromises);
      
      // Refresh the user list
      await loadAllUsers();
      setSelectedUserIds(new Set());
      
      alert(`✅ Successfully deleted ${selectedUserIds.size} user(s)`);
    } catch (error) {
      console.error('Error deleting users:', error);
      alert(`❌ Error deleting users: ${error}`);
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="view-nicknames-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👥 User Nicknames Directory</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="search-section">
            <input
              type="text"
              placeholder="🔍 Search by nickname, email, name, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button onClick={loadAllUsers} className="refresh-btn" title="Refresh list">
              🔄 Refresh
            </button>
            <button 
              onClick={handleSelectAllUsers} 
              className="select-all-btn"
              title="Select all users (including those with nicknames)"
            >
              {selectedUserIds.size === users.length ? '☑️ Deselect All' : '☐ Select All Users'}
            </button>
            
            {users.some(u => !u.nickname) && (
              <button 
                onClick={handleSelectAllWithoutNickname} 
                className="select-btn"
                title="Select all users without nicknames"
              >
                {selectedUserIds.size > 0 ? '☑️ Deselect All' : '☐ Select All Missing'}
              </button>
            )}
            
            <button 
              onClick={handleMigrateSelectedUsers} 
              className="migrate-btn" 
              disabled={migrating || deleting || selectedUserIds.size === 0}
              title="Generate nicknames for selected users"
            >
              {migrating ? '⏳ Generating...' : `🔧 Generate for ${selectedUserIds.size} Selected`}
            </button>
            
            <button 
              onClick={handleDeleteSelectedUsers} 
              className="delete-btn" 
              disabled={migrating || deleting || selectedUserIds.size === 0}
              title="Delete selected users (WARNING: This cannot be undone!)"
            >
              {deleting ? '⏳ Deleting...' : `🗑️ Delete ${selectedUserIds.size} Selected`}
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading users...</div>
          ) : (
            <>
              <div className="users-count">
                Showing {filteredUsers.length} of {users.length} users
              </div>
              
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>Select</th>
                      <th>Nickname</th>
                      <th>Display Name</th>
                      <th>Email</th>
                      <th>User ID</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="no-data">
                          {searchTerm ? 'No users match your search' : 'No users found'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr key={user.userId}>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedUserIds.has(user.userId)}
                              onChange={() => handleToggleUser(user.userId)}
                              className="user-checkbox"
                            />
                          </td>
                          <td className="nickname-cell">
                            {user.nickname ? (
                              <span className="nickname-badge">{user.nickname}</span>
                            ) : (
                              <span className="no-nickname">⚠️ No nickname</span>
                            )}
                          </td>
                          <td>{user.displayName || '-'}</td>
                          <td className="email-cell">{user.email}</td>
                          <td className="userid-cell" title={user.userId}>
                            <code className="userid-code">{user.userId.substring(0, 8)}...</code>
                          </td>
                          <td>
                            <span className={`role-badge role-${user.role.toLowerCase()}`}>
                              {user.role}
                            </span>
                          </td>
                          <td>
                            {user.nickname ? (
                              <span className="status-active">✓ Active</span>
                            ) : (
                              <span className="status-pending">⚠️ Needs nickname</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="info-section">
                <h3>📋 About Nicknames:</h3>
                <ul>
                  <li><strong>Format:</strong> 3 letters + 2 numbers (e.g., EUG01, JSM02)</li>
                  <li><strong>Uniqueness:</strong> Globally unique across all users</li>
                  <li><strong>Generation:</strong> Automatic based on name or email</li>
                  <li><strong>Purpose:</strong> Track who made changes in audit trails</li>
                  <li><strong>Permanence:</strong> Nicknames cannot be changed (data integrity)</li>
                </ul>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ViewNicknames;

